import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { mockPrendas } from '../mocks/clienteArmario';

export interface Prenda {
  id: number;
  cliente_id: number;
  nombre: string;
  categoria: string;
  color: string;
  ocasion: string[];
  imagen_url: string | null;
  frecuencia_uso: number;
  estacion: string[];
  talla: string | null;
  material: string | null;
  notas: string | null;
  activa: boolean;
  sort_order: number;
}

const STORAGE_BUCKET = 'armario';

export function useAdminArmario(initialData: Prenda[] = mockPrendas, clienteId?: number) {
  const [data, setData] = useState<Prenda[]>(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastClienteIdRef = useRef<number | undefined>(undefined);
  const isFirstMount = useRef(true);

  // On first mount or when clienteId changes: silently refetch in background
  // Keep existing data visible to avoid flash; update when response arrives
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      lastClienteIdRef.current = clienteId;
      // First load can show loading
      setLoading(true);
      setData(initialData);
      return;
    }
    if (lastClienteIdRef.current !== clienteId) {
      lastClienteIdRef.current = clienteId;
      // Only reset data to initialData if it has real content; otherwise keep stale
      if (initialData.length > 0) {
        setData(initialData);
      }
      // Silent background refetch — no loading flash
      setError(null);
    }
  }, [initialData, clienteId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let query = supabase.from('cliente_armario').select('*');
        if (clienteId !== undefined && clienteId > 0) {
          query = query.eq('cliente_id', clienteId);
        }
        const { data: rows, error: err } = await query;
        if (err) {
          setError(err.message);
        } else if (rows && rows.length > 0) {
          setData(rows as Prenda[]);
        } else if (initialData.length > 0) {
          setData(initialData);
        }
      } catch {
        setError('Error de conexión. Usando datos locales.');
        if (initialData.length > 0) setData(initialData);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  const add = async (item: Omit<Prenda, 'id'>) => {
    setLoading(true);
    try {
      const { data: inserted, error: err } = await supabase.from('cliente_armario').insert(item).select().single();
      if (err) { setError(err.message); return; }
      if (inserted) setData((prev) => [...prev, inserted as Prenda]);
    } catch { setError('Error al crear'); }
    finally { setLoading(false); }
  };

  const update = async (id: number, updates: Partial<Prenda>) => {
    setLoading(true);
    try {
      const { data: updated, error: err } = await supabase.from('cliente_armario').update(updates).eq('id', id).select().single();
      if (err) { setError(err.message); return; }
      if (updated) setData((prev) => prev.map((i) => (i.id === id ? (updated as Prenda) : i)));
    } catch { setError('Error al actualizar'); }
    finally { setLoading(false); }
  };

  const remove = async (id: number) => {
    setLoading(true);
    try {
      const prenda = data.find((p) => p.id === id);
      if (prenda?.imagen_url && prenda.imagen_url.includes('/storage/v1/object/public/')) {
        try {
          const path = prenda.imagen_url.split('/storage/v1/object/public/' + STORAGE_BUCKET + '/')[1];
          if (path) await supabase.storage.from(STORAGE_BUCKET).remove([path]);
        } catch { /* ignore storage delete errors */ }
      }
      const { error: err } = await supabase.from('cliente_armario').delete().eq('id', id);
      if (err) { setError(err.message); return; }
      setData((prev) => prev.filter((i) => i.id !== id));
    } catch { setError('Error al eliminar'); }
    finally { setLoading(false); }
  };

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase.from('cliente_armario').select('*');
      if (clienteId !== undefined && clienteId > 0) {
        query = query.eq('cliente_id', clienteId);
      }
      const { data: rows, error: err } = await query;
      if (err) setError(err.message);
      else if (rows) setData(rows as Prenda[]);
    } catch { setError('Error de conexión'); }
    finally { setLoading(false); }
  };

  const uploadImage = async (file: File, _clienteId: number): Promise<string | null> => {
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `${_clienteId}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from(STORAGE_BUCKET).upload(path, file, {
        cacheControl: '3600',
        upsert: false,
      });
      if (upErr) { setError('Error subiendo imagen: ' + upErr.message); return null; }
      const { data: urlData } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path);
      return urlData?.publicUrl ?? null;
    } catch (e) {
      setError('Error subiendo imagen');
      return null;
    }
  };

  return { data, add, update, remove, refresh, uploadImage, loading, error };
}
